import React, { useState, useRef } from 'react';
import {
  Container, Card, Typography, TextField, FormControlLabel, Checkbox,
  Button, Box, Stack, Alert, AlertColor, AlertTitle, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import SignaturePad, { SignaturePadRef } from '../Components/SignaturePad.tsx';
import PublicApiService from '../Services/publicApiService.ts';

const TattooConsentForm = () => {
  const initialFormState = {
    firstName: '',
    lastName: '',
    date: '',
    dateOfBirth: '',
    age: '',
    address: '',
    phone: '',
    email: '',
    designLocation: '',
    emergencyContact: '',
    physicianContact: '',
    idNumber: '',
    tattooArtist: '',
    acknowledgments: {} as { [key: number]: { checked: boolean; label: string } },
    artistDate: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [phoneError, setPhoneError] = useState('');
  const clientSigRef = useRef<SignaturePadRef>(null);
  const artistSigRef = useRef<SignaturePadRef>(null);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'warning',
  });

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Photo ID upload state
  const [photoIdFile, setPhotoIdFile] = useState<string | null>(null);
  const [photoIdFileName, setPhotoIdFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAlert({ open: true, message: 'Please upload an image file (JPG, PNG, etc.).', severity: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAlert({ open: true, message: 'Photo must be less than 5MB.', severity: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoIdFile(reader.result as string);
      setPhotoIdFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const removePhotoId = () => {
    setPhotoIdFile(null);
    setPhotoIdFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneNumber = e.target.value.replace(/\D/g, '');

    if (phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.slice(1);
    }

    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.slice(0, 10);
    }

    if (phoneNumber.length !== 10 && phoneNumber.length > 0) {
      setPhoneError('Phone number must be exactly 10 digits.');
    } else {
      setPhoneError('');
    }

    setFormData({ ...formData, phone: phoneNumber });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name !== 'phone') {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAcknowledgment = (index: number, label: string) => {
    setFormData({
      ...formData,
      acknowledgments: {
        ...formData.acknowledgments,
        [index]: { checked: !formData.acknowledgments[index]?.checked, label }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneError) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    if (formData.phone.length !== 10) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    // Validate all acknowledgments are checked
    const allChecked = acknowledgmentStatements.every(
      (_, index) => formData.acknowledgments[index]?.checked
    );
    if (!allChecked) {
      setAlert({ open: true, message: 'Please acknowledge all consent statements.', severity: 'error' });
      return;
    }

    // Validate client signature
    if (clientSigRef.current?.isEmpty()) {
      setAlert({ open: true, message: 'Please provide your signature.', severity: 'error' });
      return;
    }

    // Validate artist signature
    if (artistSigRef.current?.isEmpty()) {
      setAlert({ open: true, message: 'Artist signature is required.', severity: 'error' });
      return;
    }

    // Validate photo ID upload
    if (!photoIdFile) {
      setAlert({ open: true, message: 'Please upload a photo of your ID.', severity: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const apiService = new PublicApiService();
      const submitData = {
        ...formData,
        formType: 'tattooConsent',
        clientSignature: clientSigRef.current?.toDataURL() || '',
        artistSignature: artistSigRef.current?.toDataURL() || '',
        photoIdImage: photoIdFile || '',
        photoIdVerified: 'yes',
      };
      await apiService.postConsentData(submitData);

      setFormData(initialFormState);
      clientSigRef.current?.clear();
      artistSigRef.current?.clear();
      removePhotoId();
      setSuccessModalOpen(true);
    } catch (error) {
      setAlert({ open: true, message: 'Error submitting the form. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const acknowledgmentStatements = [
    'I acknowledge by signing this agreement that I have been given the full opportunity to ask any and all questions which I might have about the obtaining of a tattoo and that all of my questions have been answered to my full satisfaction.',
    'If I have any condition that might affect the healing of this tattoo, I will advise my tattooer. I am not pregnant or nursing. I am not under the influence of alcohol or drugs.',
    'I acknowledge it is not reasonably possible for the tattooer of this tattoo shop to determine whether I might have an allergic reaction to the pigments or processes used in my tattoo, and I agree to accept the risk that such a reaction is possible.',
    'I acknowledge it is not reasonably possible for the representatives and employees of this tattoo shop to determine whether I might have an allergic reaction to the pigments or processes used in my tattoo, and I agree to accept the risk that such a reaction is possible.',
    'I acknowledge that infection is always possible as a result of the obtaining of a tattoo, particularly in the event that I do not take proper care of my tattoo. I have received aftercare instructions and agree to follow them while my tattoo is healing. I agree that any touch-up work needed, due to my own negligence, will be done at my own expense.',
    'I realize that variations in color and design may exist between any tattoo as selected by me and as ultimately applied to my body. I understand that if my skin color is dark, the colors will not appear as they do on light skin.',
    'I understand that if I have any skin treatments, laser hair removal, plastic surgery or other skin altering procedures, it may result in adverse changes to my tattoo.',
    'I acknowledge that a tattoo is a permanent change to my appearance and that no representations have been made to me as to the ability to later change or remove my tattoo. To my knowledge, I do not have a physical, mental or medical impairment or disability which might affect my well being as a direct or indirect result of my decision to have a tattoo.',
    'I acknowledge I am over the age of eighteen and that I have truthfully represented to my tattooer that the obtaining of a tattoo is by my choice alone. I consent to the application of the tattoo and to any actions or conduct of the representatives and employees of the tattoo shop reasonably necessary to perform the tattoo procedure.',
    'I also understand that any time required for preparation including but not limited to the drawing and stencil time of the tattoo design that was agreed upon on the date and time of my appointment are part of the tattoo process and will be included into the charges towards my tattoo.',
    'I understand it is my responsibility to review all the details in the drawing or stencil for my tattoo including all spelling, grammar, dates or numbers and it is not the responsibility of the tattooer to ensure that it is correct apart from the usual check up before the procedure.',
    'I am aware that there may be filming done during my session. I understand that it may be used for commercial use. If I do not consent to this usage then my face will be edited out of the footage.',
    'I am aware of the level of experience that my current assigned artist has and I agree to allow them to tattoo me. I understand that tattoos are not perfect and that getting this tattoo I have no control of the overall outcome outside of the agreed design and placement. I understand my artist will do it to the best of their ability nonetheless and I am in compliance with this risk.',
    'I understand the risks of getting a tattoo; people have different reactions to tattoos. If I were to lose consciousness or have an adverse reaction I understand that the tattoo artist working is not responsible for the events that transpire. I am aware that the artist will do anything in their power to help me if that were to happen but they are not responsible for the way that my body reacts to the procedure.',
    'I acknowledge and agree that all tattoo services and session fees are non-refundable. I understand that tattoo results and healing may vary based on individual skin type, aftercare, and the natural healing process.',
    'I agree not to bring any legal action or claim against Jasel Jewelry, its owners, employees, contractors, agents, affiliated companies, or associated personnel for issues arising from or related to my voluntary decision to receive tattoo and/or body piercing services at this establishment, except to the extent such waiver or limitation is prohibited by applicable law.',
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      {alert.open && (
        <Stack sx={{ width: '100%', mb: 2 }} spacing={2}>
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, open: false })}
          >
            <AlertTitle>{alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}</AlertTitle>
            {alert.message}
          </Alert>
        </Stack>
      )}

      <Card sx={{ p: 4, boxShadow: 3 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Consent to Tattoo
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Personal Information */}
          <Stack spacing={2} direction="row">
            <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} required />
            <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} required />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Date" type="date" name="date"
              value={formData.date} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth label="Date of Birth" type="date" name="dateOfBirth"
              value={formData.dateOfBirth} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Age" name="age" value={formData.age}
              onChange={handleChange} required sx={{ minWidth: 80 }}
            />
          </Stack>

          <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} required sx={{ mt: 2 }} />

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Phone" type="tel" name="phone"
              value={formData.phone ? `+1 ${formData.phone}` : '+1 '}
              onChange={handlePhoneChange} required
              error={Boolean(phoneError)} helperText={phoneError}
            />
            <TextField fullWidth label="Email" type="email" name="email" value={formData.email} onChange={handleChange} required />
          </Stack>

          <TextField
            fullWidth label="Design/Location of Tattoo" name="designLocation"
            value={formData.designLocation} onChange={handleChange} required
            sx={{ mt: 2 }}
          />

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField fullWidth label="Emergency Contact" name="emergencyContact" value={formData.emergencyContact} onChange={handleChange} required />
            <TextField fullWidth label="Physician Contact" name="physicianContact" value={formData.physicianContact} onChange={handleChange} />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField fullWidth label="ID Number" name="idNumber" value={formData.idNumber} onChange={handleChange} required />
            <TextField fullWidth label="Tattoo Artist" name="tattooArtist" value={formData.tattooArtist} onChange={handleChange} required />
          </Stack>

          {/* Consent & Acknowledgment */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Consent & Acknowledgment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            I hereby acknowledge that I have read and understood all of the below:
          </Typography>

          <Stack spacing={1.5}>
            {acknowledgmentStatements.map((statement, index) => (
              <Card key={index} sx={{ display: 'flex', alignItems: 'flex-start', p: 2, boxShadow: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acknowledgments[index]?.checked || false}
                      onChange={() => handleAcknowledgment(index, statement)}
                      sx={{ mr: 1 }}
                    />
                  }
                  label={<Typography variant="body2">{statement}</Typography>}
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
              </Card>
            ))}
          </Stack>

          {/* Signatures */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Signatures
          </Typography>

          {/* Client Signature */}
          <Box sx={{ mb: 3 }}>
            <SignaturePad ref={clientSigRef} label="Client Signature" />
            <TextField
              label="Date" type="date" name="date"
              value={formData.date} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
              sx={{ ml: 2, mt: 1, minWidth: 180 }}
              size="small"
              disabled
            />
          </Box>

          {/* Artist Signature */}
          <Box sx={{ mb: 3 }}>
            <SignaturePad ref={artistSigRef} label="Artist Signature" />
          </Box>

          {/* Photo ID Upload */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Photo ID
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please upload a clear photo of your government-issued ID (driver's license, passport, etc.)
          </Typography>

          {/* Hidden file inputs */}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />

          {!photoIdFile ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Photo
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => cameraInputRef.current?.click()}
              >
                Take Photo
              </Button>
            </Stack>
          ) : (
            <Box>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                <img
                  src={photoIdFile}
                  alt="Photo ID"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '250px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                  }}
                />
                <IconButton
                  onClick={removePhotoId}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                  }}
                  size="small"
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {photoIdFileName}
              </Typography>
            </Box>
          )}

          {/* Submit */}
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 4 }} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Consent Form'}
          </Button>
        </Box>
      </Card>
      {/* Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title">✅ Form Submitted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your tattoo consent form has been submitted successfully! Thank you.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessModalOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TattooConsentForm;
